// Manual measurement entry — driven entirely by the assessment's own template (segment types,
// their dimensions, and any CAPTURED — i.e. not derivable — measurement fields), never hardcoded
// field names. A segment's derivable fields (e.g. a Roof Plane's area = length × width) are
// computed server-side from the submitted dimensions (see app.domain.assessments.service
// .add_segment's derivation pass) — this form only needs to collect raw dimensions plus whatever
// can't be derived (e.g. a Roof Plane's pitch, which is observed, not computed).
import { useState } from 'react';
import { useGetAssessmentTemplateByCodeQuery } from '@/redux/internal-services/assessmentsApi';
import {
  useAddAssessmentSegmentMutation, useUpdateAssessmentSegmentMutation, type Segment,
} from '@/redux/internal-services/assessmentsApi';
import { Button } from '@/components/UI/Button';

const inputCls = 'w-full rounded-md border border-ui-border bg-ui-input px-2 py-0.5 !text-xs text-ui-text font-poppins focus:outline-none focus:ring-1 focus:ring-ui-ring/30';

interface SegmentEntryFormProps {
  assessmentId: string;
  templateCode: string;
  templateVersion: string;
  /** Set to edit an existing hand-entered segment in place instead of adding a new one. The form
   *  is the same either way — the same fields, the same derivation — so editing reuses it rather
   *  than growing a second, subtly different one. */
  editSegment?: Segment | null;
  onDone: () => void;
}

const humanizeCode = (code: string) => code.replace(/_/g, ' ').replace(/^\w/, (c) => c.toUpperCase());
const fmtNum = (v: unknown) =>
  typeof v === 'number' ? String(Number.isInteger(v) ? v : Math.round(v * 100) / 100) : String(v ?? '');

/** The hand-entered segments on an assessment, with their real numbers — shown both inside the
 *  capture panel (immediate feedback while typing them) and as a standing section on the card
 *  next to Measurement file, so what a person measured by hand is visible without reopening the
 *  panel. Values are read back as STORED: the server derives a plane's area from its dimensions.
 */
export function ManualSegmentList({
  segments, segmentTypeNames,
}: { segments: Segment[]; segmentTypeNames?: Record<string, string> }) {
  if (segments.length === 0) return null;
  return (
    <div className="space-y-1">
      {segments.map((seg) => {
        const typeName = segmentTypeNames?.[seg.segment_type_code] ?? humanizeCode(seg.segment_type_code);
        const dims = seg.dimensions
          .filter((d) => d.value !== null && d.value !== undefined && d.value !== '')
          .map((d) => `${humanizeCode(d.dimension_code)} ${fmtNum(d.value)}${d.uom ? ` ${d.uom}` : ''}`)
          .join(' × ');
        const vals = seg.segment_measurement_values
          .map((v) => `${humanizeCode(v.field_code)} ${fmtNum(v.value)}${v.uom ? ` ${v.uom}` : ''}`)
          .join(' · ');
        return (
          <p key={seg.segment_code} className="text-xs text-ui-text2">
            <span className="font-medium text-ui-text">{typeName}</span>
            {dims && ` · ${dims}`}
            {vals && <span className="text-ui-text3"> → {vals}</span>}
          </p>
        );
      })}
    </div>
  );
}

export function SegmentEntryForm({
  assessmentId, templateCode, templateVersion, editSegment, onDone,
}: SegmentEntryFormProps) {
  const { data: template, isLoading } = useGetAssessmentTemplateByCodeQuery({ code: templateCode, version: templateVersion });
  const [addSegment, { isLoading: isAdding }] = useAddAssessmentSegmentMutation();
  const [updateSegment, { isLoading: isUpdating }] = useUpdateAssessmentSegmentMutation();
  const isEditing = !!editSegment;

  const segmentTypes = template?.segment_types ?? [];
  const [typeCode, setTypeCode] = useState(editSegment?.segment_type_code ?? '');
  const [values, setValues] = useState<Record<string, string>>(() => {
    if (!editSegment) return {};
    const seeded: Record<string, string> = {};
    for (const d of editSegment.dimensions) seeded[d.dimension_code] = String(d.value ?? '');
    // Only CAPTURED values are seeded back: a derived one (a plane's area) is recomputed from the
    // dimensions on save, so showing it as an editable field would invite an edit that is silently
    // discarded.
    for (const v of editSegment.segment_measurement_values) {
      if (v.value_origin === 'CAPTURED') seeded[v.field_code] = String(v.value ?? '');
    }
    return seeded;
  });
  const [error, setError] = useState('');

  const activeType = segmentTypes.find((s) => s.segment_type_code === (typeCode || segmentTypes[0]?.segment_type_code));
  const capturedFields = (activeType?.segment_measurement_fields ?? []).filter((f) => f.usage === 'CAPTURED');

  const setValue = (code: string, v: string) => setValues((prev) => ({ ...prev, [code]: v }));

  const submit = async () => {
    if (!activeType) return;
    setError('');
    const dimensions = activeType.dimensions
      .filter((d) => values[d.dimension_code]?.trim())
      .map((d) => ({ dimension_code: d.dimension_code, value: Number(values[d.dimension_code]), uom: d.uom }));
    const missingRequired = activeType.dimensions.some((d) => d.required && !values[d.dimension_code]?.trim());
    if (missingRequired) {
      setError('Fill in all required fields for this segment type.');
      return;
    }
    const segment_measurement_values = capturedFields
      .filter((f) => values[f.field_code]?.trim())
      .map((f) => ({ field_code: f.field_code, value: values[f.field_code], uom: f.uom || undefined }));
    try {
      const payload = {
        assessment_id: assessmentId,
        segment_type_code: activeType.segment_type_code,
        dimensions,
        segment_measurement_values: segment_measurement_values.length ? segment_measurement_values : undefined,
      };
      if (editSegment) {
        await updateSegment({ ...payload, segment_code: editSegment.segment_code }).unwrap();
        onDone();
        return;
      }
      await addSegment(payload).unwrap();
      setValues({});
    } catch (e) {
      setError((e as { data?: { message?: string } })?.data?.message || 'Could not add that segment.');
    }
  };

  if (isLoading) return <p className="text-xs text-ui-text3">Loading assessment template…</p>;
  if (!activeType) return <p className="text-xs text-ui-text3">This template has no segment types configured.</p>;



  return (
    <div className="space-y-1.5 rounded-lg border border-ui-border bg-ui-subtle p-2">
      <div>
        <label className="mb-0.5 block text-xs font-medium text-ui-text">Segment type</label>
        <select
          value={activeType.segment_type_code}
          onChange={(e) => { setTypeCode(e.target.value); setValues({}); setError(''); }}
          className={inputCls}
        >
          {segmentTypes.map((s) => (
            <option key={s.segment_type_code} value={s.segment_type_code}>{s.segment_type_name}</option>
          ))}
        </select>
      </div>

      <div className="grid grid-cols-2 gap-1.5">
        {activeType.dimensions.map((d) => (
          <div key={d.dimension_code}>
            <label className="mb-0.5 block text-xs text-ui-text3">
              {d.dimension_name} ({d.uom}){d.required && <span className="text-danger-text"> *</span>}
            </label>
            <input
              type="number"
              step="0.01"
              value={values[d.dimension_code] || ''}
              onChange={(e) => setValue(d.dimension_code, e.target.value)}
              className={inputCls}
            />
          </div>
        ))}
        {capturedFields.map((f) => (
          <div key={f.field_code}>
            <label className="mb-0.5 block text-xs text-ui-text3">{f.field_name}</label>
            <input
              value={values[f.field_code] || ''}
              onChange={(e) => setValue(f.field_code, e.target.value)}
              placeholder={f.field_code === 'roof_pitch' ? 'e.g. 8/12' : undefined}
              className={inputCls}
            />
          </div>
        ))}
      </div>

      {error && <p className="text-xs text-danger-text">{error}</p>}

      <div className="flex justify-end gap-2">
        <Button type="button" variant="ghost" size="sm" onClick={onDone} className="h-6 md:h-7 min-h-0 px-3 text-xs" title="Closes this panel without saving the fields above">Close</Button>
        <Button type="button" size="sm" disabled={isAdding || isUpdating} onClick={() => void submit()} className="h-6 md:h-7 min-h-0 px-3 text-xs">
          {isEditing
            ? (isUpdating ? 'Saving…' : 'Save changes')
            : (isAdding ? 'Adding…' : 'Add segment')}
        </Button>
      </div>
    </div>
  );
}

export default SegmentEntryForm;
